'use client';

import { useMemo, useCallback } from 'react';
import { useDebounce } from 'use-debounce';
import useGlobalSearch from 'src/hooks/globalSearch/useGlobalSearch';
import { useGetCodeSuggestionsQuery } from 'src/api/redux/codes';
import { useGetFilterSetsQuery } from 'src/api/redux/filterSets';
import {
  useGetPayerTypeSuggestionsInfiniteQuery,
  useGetPayerNameSuggestionsInfiniteQuery,
  useGetCrmStatusesSuggestionsQuery,
  useGetOpenPaymentsSuggestionsInfiniteQuery,
  useGetOpenPaymentsCategorySuggestionsInfiniteQuery,
  useGetEducationSuggestionsInfiniteQuery,
} from 'src/api/redux/suggestions';
import { useGetProviderCohortsQuery } from 'src/api/redux/providerCohorts';
import { useGetListsQuery } from 'src/api/redux/lists';
import { CriteriaComponentType } from 'src/redux/slices/aiListGeneratorSlice';
import { stripHtml } from 'src/utils/stripHtml';
import { MIN_SEARCH_LENGTH } from '../constants';

// Transform functions from existing configs
import { transformProvider } from '../components/Providers/config';
import { transformCode } from '../components/Codes/config';
import { transformPayerType } from '../components/PayerType/config';
import { transformPayerName } from '../components/PayerName/config';
import { transformCRMStatus } from '../components/CRMStatus/config';
import { transformOpenPayment } from '../components/OpenPayments/config';
import { transformFilterSet } from '../components/FilterSets/config';
import { transformEncounter } from '../components/Encounters/config';
import { transformCodeSet } from '../components/CodeSets/config';
import { transformProviderList } from '../components/ProviderLists/config';
import { transformEducationalInstitution } from '../components/EducationalInstitutions/config';

/**
 * Unified search result that can represent any criteria type
 */
export interface UnifiedSearchResult {
  id: string;
  displayLabel: string;
  secondaryLabel?: string | null;
  criteriaType: CriteriaComponentType;
  colorTheme: string;
   
  originalData: Record<string, any>;
}

/**
 * Grouped search results by criteria type
 */
export interface GroupedSearchResults {
  providers: UnifiedSearchResult[];
  educational_institution: UnifiedSearchResult[];
  codes: UnifiedSearchResult[];
  payer_type: UnifiedSearchResult[];
  payer_name: UnifiedSearchResult[];
  crm_status: UnifiedSearchResult[];
  open_payments: UnifiedSearchResult[];
  open_payments_category: UnifiedSearchResult[];
  filter_set: UnifiedSearchResult[];
  code_set: UnifiedSearchResult[];
  provider_cohort: UnifiedSearchResult[];
  provider_list: UnifiedSearchResult[];
  this_provider: UnifiedSearchResult[];
  this_site: UnifiedSearchResult[];
}

/**
 * Loading state per criteria type
 */
export interface LoadingStates {
  providers: boolean;
  educational_institution: boolean;
  codes: boolean;
  payer_type: boolean;
  payer_name: boolean;
  crm_status: boolean;
  open_payments: boolean;
  open_payments_category: boolean;
  filter_set: boolean;
  code_set: boolean;
  provider_cohort: boolean;
  provider_list: boolean;
  this_provider: boolean;
  this_site: boolean;
}

interface UseUnifiedCriteriaSearchOptions {
  allowedTypes?: CriteriaComponentType[];
  maxResultsPerType?: number;
}

interface UseUnifiedCriteriaSearchResult {
  results: GroupedSearchResults;
  isLoading: boolean;
  isPartialLoading: boolean;
  loadingStates: LoadingStates;
  hasResults: boolean;
}

const EMPTY_RESULTS: GroupedSearchResults = {
  providers: [],
  educational_institution: [],
  codes: [],
  payer_type: [],
  payer_name: [],
  crm_status: [],
  open_payments: [],
  open_payments_category: [],
  filter_set: [],
  code_set: [],
  provider_cohort: [],
  provider_list: [],
  this_provider: [],
  this_site: [],
};

const DEFAULT_MAX_RESULTS = 5;

/**
 * Hook that searches across all criteria types and returns unified, grouped results.
 *
 * Only allowed types are queried and returned. Code sets and filter sets share one
 * filter-sets request, which runs when either type is allowed.
 *
 * Loading: `loadingStates` reports each allowed type's in-flight request (RTK
 * `isFetching`, so later searches count, not only the first). `isLoading` is true
 * while the term is still debouncing or any allowed type is fetching, so callers
 * can tell "still searching" apart from "no results".
 */
const useUnifiedCriteriaSearch = (
  searchTerm: string,
  options: UseUnifiedCriteriaSearchOptions = {}
): UseUnifiedCriteriaSearchResult => {
  const { allowedTypes, maxResultsPerType = DEFAULT_MAX_RESULTS } = options;
  const [debouncedSearch] = useDebounce(searchTerm, 300);

  const skip = !debouncedSearch || debouncedSearch.length < MIN_SEARCH_LENGTH;
  const isDebouncing =
    searchTerm.length >= MIN_SEARCH_LENGTH && searchTerm !== debouncedSearch;

  // Helper to check if type is allowed
  const isTypeAllowed = useCallback(
    (type: CriteriaComponentType) =>
      !allowedTypes || allowedTypes.includes(type),
    [allowedTypes]
  );

  // Providers
  // TODO: Migrate to RTK Query
  const { data: providersData, isFetching: providersFetching } = useGlobalSearch(
    isTypeAllowed('providers') && !skip ? debouncedSearch : ''
  );

  // Educational Institutions
  const {
    data: educationalInstitutionData,
    isFetching: educationalInstitutionFetching,
  } = useGetEducationSuggestionsInfiniteQuery(
    { q: debouncedSearch },
    { skip: skip || !isTypeAllowed('educational_institution') }
  );
  const educationalInstitutionResults = useMemo(
    () => educationalInstitutionData?.pages?.flatMap((p) => p.results ?? []) ?? [],
    [educationalInstitutionData?.pages]
  );

  // Codes
  const { data: codesData, isFetching: codesFetching } = useGetCodeSuggestionsQuery(
    { q: debouncedSearch },
    { skip: skip || !isTypeAllowed('codes') }
  );

  // Payer Types
  const { data: payerTypeData, isFetching: payerTypeFetching } =
    useGetPayerTypeSuggestionsInfiniteQuery(
      { q: debouncedSearch },
      { skip: skip || !isTypeAllowed('payer_type') }
    );
  const payerTypeResults = useMemo(
    () => payerTypeData?.pages?.flatMap((p) => p.results ?? []) ?? [],
    [payerTypeData?.pages]
  );

  // Payer Names
  const { data: payerNameData, isFetching: payerNameFetching } =
    useGetPayerNameSuggestionsInfiniteQuery(
      { q: debouncedSearch },
      { skip: skip || !isTypeAllowed('payer_name') }
    );
  const payerNameResults = useMemo(
    () => payerNameData?.pages?.flatMap((p) => p.results ?? []) ?? [],
    [payerNameData?.pages]
  );

  // CRM Statuses (returns array directly, filter locally)
  const { data: allCrmStatuses, isFetching: crmStatusFetching } =
    useGetCrmStatusesSuggestionsQuery(
      { q: '' },
      { skip: skip || !isTypeAllowed('crm_status') }
    );
  const crmStatusResults = useMemo(() => {
    if (!allCrmStatuses || !debouncedSearch) return allCrmStatuses || [];
    const searchLower = debouncedSearch.toLowerCase();
    return allCrmStatuses.filter((status) =>
      `${status.crm_type} ${status.crm_status}`.toLowerCase().includes(searchLower)
    );
  }, [allCrmStatuses, debouncedSearch]);

  // Open Payments
  const { data: openPaymentsData, isFetching: openPaymentsFetching } =
    useGetOpenPaymentsSuggestionsInfiniteQuery(
      { q: debouncedSearch },
      { skip: skip || !isTypeAllowed('open_payments') }
    );
  const openPaymentsResults = useMemo(
    () => openPaymentsData?.pages?.flatMap((p) => p.results ?? []) ?? [],
    [openPaymentsData?.pages]
  );

  // Open Payments Categories
  const {
    data: openPaymentsCategoryData,
    isFetching: openPaymentsCategoryFetching,
  } = useGetOpenPaymentsCategorySuggestionsInfiniteQuery(
    { q: debouncedSearch },
    { skip: skip || !isTypeAllowed('open_payments_category') }
  );
  const openPaymentsCategoryResults = useMemo(
    () => openPaymentsCategoryData?.pages?.flatMap((p) => p.results ?? []) ?? [],
    [openPaymentsCategoryData?.pages]
  );

  // Filter Sets and Code Sets (one request; code sets are filter sets with type CODE)
  const isFilterSetAllowed = isTypeAllowed('filter_set');
  const isCodeSetAllowed = isTypeAllowed('code_set');
  const { data: filterSetData, isFetching: filterSetFetching } = useGetFilterSetsQuery(
    { q: debouncedSearch },
    { skip: skip || !(isFilterSetAllowed || isCodeSetAllowed) }
  );

  // Provider Lists
  const { data: providerListsData, isFetching: providerListsFetching } =
    useGetListsQuery(
      {},
      { skip: skip || !isTypeAllowed('provider_list') }
    );

  // Provider Cohorts / Encounters
  const { data: cohortsData, isFetching: cohortsFetching } =
    useGetProviderCohortsQuery(undefined, {
      skip: skip || !isTypeAllowed('provider_cohort'),
    });

  // Transform and group results
  const results = useMemo<GroupedSearchResults>(() => {
    if (skip) return EMPTY_RESULTS;

    return {
      providers: (providersData || [])
        .slice(0, maxResultsPerType)
        .map((item: Parameters<typeof transformProvider>[0]) => {
          const transformed = transformProvider(item);
          return {
            id: transformed.id,
            displayLabel: stripHtml(transformed.displayLabel),
            secondaryLabel: transformed.specialty
              ? stripHtml(transformed.specialty)
              : null,
            criteriaType: 'providers' as CriteriaComponentType,
            colorTheme: 'blue',
            originalData: transformed,
          };
        }),

      educational_institution: educationalInstitutionResults
        .slice(0, maxResultsPerType)
        .map((item: Parameters<typeof transformEducationalInstitution>[0]) => {
          const transformed = transformEducationalInstitution(item);
          return {
            id: transformed.id,
            displayLabel: stripHtml(transformed.displayLabel),
            secondaryLabel: null,
            criteriaType: 'educational_institution' as CriteriaComponentType,
            colorTheme: 'amber',
            originalData: transformed,
          };
        }),

      codes: (codesData && 'results' in codesData ? codesData.results : Array.isArray(codesData) ? codesData : [])
        .slice(0, maxResultsPerType)
        .map((item: Parameters<typeof transformCode>[0]) => {
          const transformed = transformCode(item);
          return {
            id: transformed.id,
            displayLabel: stripHtml(transformed.displayLabel),
            secondaryLabel: transformed.category
              ? stripHtml(transformed.category)
              : null,
            criteriaType: 'codes' as CriteriaComponentType,
            colorTheme: 'green',
            originalData: transformed,
          };
        }),

      payer_type: payerTypeResults
        .slice(0, maxResultsPerType)
        .map((item: Parameters<typeof transformPayerType>[0]) => {
          const transformed = transformPayerType(item);
          return {
            id: transformed.id,
            displayLabel: stripHtml(transformed.displayLabel),
            secondaryLabel: null,
            criteriaType: 'payer_type' as CriteriaComponentType,
            colorTheme: 'pink',
            originalData: transformed,
          };
        }),

      payer_name: payerNameResults
        .slice(0, maxResultsPerType)
        .map((item: Parameters<typeof transformPayerName>[0]) => {
          const transformed = transformPayerName(item);
          return {
            id: transformed.id,
            displayLabel: stripHtml(transformed.displayLabel),
            secondaryLabel: null,
            criteriaType: 'payer_name' as CriteriaComponentType,
            colorTheme: 'sky',
            originalData: transformed,
          };
        }),

      crm_status: crmStatusResults
        .slice(0, maxResultsPerType)
        .map((item: Parameters<typeof transformCRMStatus>[0]) => {
          const transformed = transformCRMStatus(item);
          return {
            id: transformed.id,
            displayLabel: stripHtml(transformed.displayLabel),
            secondaryLabel: null,
            criteriaType: 'crm_status' as CriteriaComponentType,
            colorTheme: 'teal',
            originalData: transformed,
          };
        }),

      open_payments: openPaymentsResults
        .slice(0, maxResultsPerType)
        .map((item: Parameters<typeof transformOpenPayment>[0]) => {
          const transformed = transformOpenPayment(item);
          return {
            id: transformed.id,
            displayLabel: stripHtml(transformed.displayLabel),
            secondaryLabel: null,
            criteriaType: 'open_payments' as CriteriaComponentType,
            colorTheme: 'amber',
            originalData: transformed,
          };
        }),

      open_payments_category: openPaymentsCategoryResults
        .slice(0, maxResultsPerType)
        .map((item: Parameters<typeof transformOpenPayment>[0]) => {
          const transformed = transformOpenPayment(item);
          return {
            id: transformed.id,
            displayLabel: stripHtml(transformed.displayLabel),
            secondaryLabel: null,
            criteriaType: 'open_payments_category' as CriteriaComponentType,
            colorTheme: 'purple',
            originalData: transformed,
          };
        }),

      filter_set: (isFilterSetAllowed ? filterSetData?.results ?? [] : [])
        .filter((item: { type?: string }) => item.type !== 'CODE')
        .slice(0, maxResultsPerType)
        .map((item) => {
          const transformed = transformFilterSet(item as any);
          return {
            id: transformed.id,
            displayLabel: stripHtml(transformed.displayLabel),
            secondaryLabel: null,
            criteriaType: 'filter_set' as CriteriaComponentType,
            colorTheme: 'indigo',
            originalData: transformed,
          };
        }),

      code_set: (isCodeSetAllowed ? filterSetData?.results ?? [] : [])
        .filter((item: { type?: string }) => item.type === 'CODE')
        .slice(0, maxResultsPerType)
        .map((item) => {
          const transformed = transformCodeSet(item as any);
          return {
            id: transformed.id,
            displayLabel: stripHtml(transformed.displayLabel),
            secondaryLabel: null,
            criteriaType: 'code_set' as CriteriaComponentType,
            colorTheme: 'pink',
            originalData: transformed,
          };
        }),

      provider_cohort: (cohortsData?.results || [])
        .filter((item: { label?: string; name?: string }) => {
          const label = (item.label || item.name || '').toLowerCase();
          return label.includes(debouncedSearch.toLowerCase());
        })
        .slice(0, maxResultsPerType)
        .map((item: Parameters<typeof transformEncounter>[0]) => {
          const transformed = transformEncounter(item);
          return {
            id: transformed.id,
            displayLabel: stripHtml(transformed.displayLabel),
            secondaryLabel: null,
            criteriaType: 'provider_cohort' as CriteriaComponentType,
            colorTheme: 'orange',
            originalData: transformed,
          };
        }),

      provider_list: (providerListsData?.results || [])
        .filter((item: { name?: string }) => {
          if (!debouncedSearch) return true;
          const name = (item.name || '').toLowerCase();
          return name.includes(debouncedSearch.toLowerCase());
        })
        .slice(0, maxResultsPerType)
        .map((item: Parameters<typeof transformProviderList>[0]) => {
          const transformed = transformProviderList(item);
          return {
            id: transformed.id,
            displayLabel: stripHtml(transformed.displayLabel),
            secondaryLabel:
              transformed.num_items != null
                ? `${transformed.num_items} providers`
                : null,
            criteriaType: 'provider_list' as CriteriaComponentType,
            colorTheme: 'lime',
            originalData: transformed,
          };
        }),

      // Static context options - show when search term matches
      this_provider:
        isTypeAllowed('this_provider') &&
        ['this', 'this provider', 'provider', 'current'].some((term) =>
          term.includes(debouncedSearch.toLowerCase())
        )
          ? [
              {
                id: 'this_provider',
                displayLabel: 'This Provider',
                secondaryLabel: 'Reference the current provider profile',
                criteriaType: 'this_provider' as CriteriaComponentType,
                colorTheme: 'purple',
                originalData: { type: 'this_provider' },
              },
            ]
          : [],

      this_site:
        isTypeAllowed('this_site') &&
        ['this', 'this site', 'site', 'current', 'location'].some((term) =>
          term.includes(debouncedSearch.toLowerCase())
        )
          ? [
              {
                id: 'this_site',
                displayLabel: 'This Site',
                secondaryLabel: 'Reference the current site profile',
                criteriaType: 'this_site' as CriteriaComponentType,
                colorTheme: 'teal',
                originalData: { type: 'this_site' },
              },
            ]
          : [],
    };
  }, [
    skip,
    providersData,
    maxResultsPerType,
    educationalInstitutionResults,
    codesData,
    payerTypeResults,
    payerNameResults,
    crmStatusResults,
    openPaymentsResults,
    openPaymentsCategoryResults,
    filterSetData?.results,
    isFilterSetAllowed,
    isCodeSetAllowed,
    cohortsData?.results,
    providerListsData?.results,
    isTypeAllowed,
    debouncedSearch,
  ]);

  const loadingStates = useMemo<LoadingStates>(
    () => ({
      providers: providersFetching,
      educational_institution: educationalInstitutionFetching,
      codes: codesFetching,
      payer_type: payerTypeFetching,
      payer_name: payerNameFetching,
      crm_status: crmStatusFetching,
      open_payments: openPaymentsFetching,
      open_payments_category: openPaymentsCategoryFetching,
      filter_set: filterSetFetching && isFilterSetAllowed,
      code_set: filterSetFetching && isCodeSetAllowed,
      provider_cohort: cohortsFetching,
      provider_list: providerListsFetching,
      this_provider: false, // Static options, no loading state
      this_site: false, // Static options, no loading state
    }),
    [
      providersFetching,
      educationalInstitutionFetching,
      codesFetching,
      payerTypeFetching,
      payerNameFetching,
      crmStatusFetching,
      openPaymentsFetching,
      openPaymentsCategoryFetching,
      filterSetFetching,
      isFilterSetAllowed,
      isCodeSetAllowed,
      cohortsFetching,
      providerListsFetching,
    ]
  );

  return useMemo(() => {
    const isPartialLoading = Object.values(loadingStates).some(Boolean);
    return {
      results,
      isLoading: isDebouncing || isPartialLoading,
      isPartialLoading,
      loadingStates,
      hasResults: Object.values(results).some((arr) => arr.length > 0),
    };
  }, [results, isDebouncing, loadingStates]);
};

export default useUnifiedCriteriaSearch;
